import React, { useState } from "react";
import { ArrowRight, WhatsappLogo, CheckCircle, Check, CreditCard, ShieldCheck } from "@phosphor-icons/react";
import Countdown from "./Countdown";
import { submitLead, createCheckoutSession } from "../lib/api";
import { openWhatsapp } from "../lib/whatsapp";
import { toast } from "sonner";

const FEATURES = [
  "All 3 packages — Bronze / Silver / Gold",
  "Live trading support · 3 days a week",
  "EOD level-drawing routine",
  "Previous batch recordings",
  "MCX Premium group access",
  "GoCharting templates",
  "WhatsApp + Telegram communities",
  "Personal onboarding & guidance",
];

const EXP_OPTIONS = ["Beginner", "1–3 yrs", "3–5 yrs", "5+ yrs"];

export default function Enroll() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "Beginner",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const payNow = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill name, email and phone before paying.");
      return;
    }
    setPayLoading(true);
    try {
      // Capture as lead first (best-effort, do not block on failure)
      try {
        await submitLead({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          experience: form.experience,
          message: form.message.trim() || "Initiated Stripe checkout",
        });
      } catch (_) {}

      const res = await createCheckoutSession({
        package_id: "mp_of_full",
        origin_url: window.location.origin,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout. Try WhatsApp.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Payment could not start. Try WhatsApp."
      );
    } finally {
      setPayLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        experience: form.experience,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Reserved! Our team will reach out within 24 hours.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not submit. Try WhatsApp instead."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="enroll-section"
      id="enroll"
      className="py-20 lg:py-28 border-t border-[#D4D0C5] bg-[#EAE8DF]"
    >
      <div className="max-w-[1320px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        {/* LEFT - Pricing */}
        <div className="lg:col-span-5 bg-[#0A0A0A] text-[#F4F3ED] p-8 lg:p-10 border border-[#0A0A0A] flex flex-col">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
            08 / TALK TO US
          </div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95] mb-6">
            Not sure
            <br />
            which package
            <br />
            fits you?
          </h2>

          <div className="mt-2 mb-6">
            <div className="flex items-end gap-3">
              <span className="font-display font-black text-6xl lg:text-7xl tracking-tighter leading-none text-[#F4F3ED]">
                From&nbsp;₹20K
              </span>
            </div>
            <div className="font-mono-acc text-[10px] tracking-[0.3em] text-[#A1A1AA] uppercase mt-2">
              Starter · Core · Pro — pick your path above
            </div>
          </div>

          <ul className="space-y-2.5 mb-6">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex items-center gap-3 font-body text-sm">
                <Check size={16} weight="bold" className="text-[#D9381E] shrink-0" />
                <span className="text-[#F4F3ED]">{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-auto pt-6 border-t border-[#2A2A2A]">
            <Countdown compact />
          </div>

          <button
            data-testid="whatsapp-btn"
            onClick={() => openWhatsapp()}
            className="mt-6 inline-flex items-center justify-center gap-3 bg-[#25D366] text-[#0A0A0A] px-6 py-3.5 font-body text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#1ebd5b] transition-colors cursor-pointer"
          >
            <WhatsappLogo size={20} weight="fill" />
            Chat on WhatsApp
          </button>
        </div>

        {/* RIGHT - Lead Form */}
        <div className="lg:col-span-7 bg-[#F4F3ED] border border-[#0A0A0A] p-8 lg:p-10">
          {!done ? (
            <>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
                    LEAD.FORM
                  </div>
                  <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">
                    Reserve a seat
                  </h3>
                </div>
                <span className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] border border-[#D4D0C5] px-2 py-1">
                  Limited Seats
                </span>
              </div>

              <p className="font-body text-sm text-[#4A4A4A] mt-2 mb-6">
                Drop your details. Our team gets in touch within 24 hours to
                onboard you, share the syllabus PDF and confirm your seat.
              </p>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="lead-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Full Name *
                    </label>
                    <input
                      data-testid="input-name"
                      required
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      placeholder="e.g. Vishnu G."
                      className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] rounded-none focus:outline-none focus:border-[#D9381E] focus:ring-0 font-body text-sm"
                    />
                  </div>
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Phone *
                    </label>
                    <input
                      data-testid="input-phone"
                      required
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="+91 ••••• •••••"
                      className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] rounded-none focus:outline-none focus:border-[#D9381E] font-body text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                    Email *
                  </label>
                  <input
                    data-testid="input-email"
                    required
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@email.com"
                    className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] rounded-none focus:outline-none focus:border-[#D9381E] font-body text-sm"
                  />
                </div>

                <div>
                  <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                    Trading Experience
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {EXP_OPTIONS.map((o) => (
                      <button
                        key={o}
                        type="button"
                        data-testid={`exp-${o}`}
                        onClick={() => setForm((p) => ({ ...p, experience: o }))}
                        className={`px-3 py-2.5 border text-xs font-body tracking-[0.15em] uppercase transition-colors ${
                          form.experience === o
                            ? "bg-[#0A0A0A] text-[#F4F3ED] border-[#0A0A0A]"
                            : "bg-[#F4F3ED] text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#EAE8DF]"
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                    Anything we should know? (optional)
                  </label>
                  <textarea
                    data-testid="input-message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={3}
                    placeholder="Your goals, instruments you trade, doubts…"
                    className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] rounded-none focus:outline-none focus:border-[#D9381E] font-body text-sm resize-none"
                  />
                </div>

                <button
                  data-testid="submit-lead-btn"
                  type="submit"
                  disabled={loading || payLoading}
                  className="w-full bg-[#0A0A0A] text-[#F4F3ED] py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
                >
                  {loading ? "Reserving…" : "Reserve my seat (talk to us)"}
                  {!loading && <ArrowRight weight="bold" size={16} />}
                </button>

                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-[#D4D0C5]" />
                  <span className="font-mono-acc text-[10px] tracking-[0.3em] text-[#4A4A4A] uppercase">
                    OR
                  </span>
                  <div className="flex-1 h-px bg-[#D4D0C5]" />
                </div>

                <button
                  data-testid="see-packages-btn"
                  type="button"
                  onClick={() =>
                    document
                      .getElementById("packages")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="w-full bg-[#D9381E] text-[#F4F3ED] py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#0A0A0A] transition-colors inline-flex items-center justify-center gap-3 border border-[#D9381E] hover:border-[#0A0A0A]"
                >
                  <CreditCard weight="bold" size={18} />
                  See packages & pay instantly
                </button>

                <p className="font-mono-acc text-[10px] tracking-[0.15em] text-[#4A4A4A] uppercase text-center inline-flex items-center justify-center gap-2">
                  <ShieldCheck size={12} weight="duotone" className="text-[#D9381E]" />
                  Secure payment via Stripe · Cards · UPI · Netbanking · EMI
                </p>
              </form>
            </>
          ) : (
            <div
              data-testid="lead-success"
              className="flex flex-col items-center text-center py-8"
            >
              <CheckCircle size={64} weight="duotone" className="text-[#D9381E] mb-5" />
              <h3 className="font-display font-black text-3xl tracking-tighter mb-3">
                Seat reserved.
              </h3>
              <p className="font-body text-[#4A4A4A] max-w-md mb-6">
                Welcome to the next cohort. Our team will reach out to{" "}
                <span className="font-semibold text-[#0A0A0A]">{form.email}</span>{" "}
                within 24 hours with onboarding details.
              </p>
              <button
                onClick={() => openWhatsapp()}
                className="inline-flex items-center gap-3 bg-[#25D366] text-[#0A0A0A] px-6 py-3 font-body text-sm uppercase tracking-[0.22em] font-bold cursor-pointer"
              >
                <WhatsappLogo size={18} weight="fill" />
                Talk to us now
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
